import React, { useState, useEffect, useRef } from 'react';
import './BotCursor.css';

/**
 * BotCursor - Visual cursor that follows bot navigation
 * Shows where the bot is "looking" or navigating to
 * Animates smoothly from current position to target
 */
const BotCursor = () => {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isActive, setIsActive] = useState(false);
  const [action, setAction] = useState('idle'); // idle, pointing, clicking, moving
  const [trail, setTrail] = useState([]);
  const currentSection = useRef(null);

  // Define cursor movement functions first
  const animateToTarget = React.useCallback((targetX, targetY, element) => {
    setAction('moving');
    
    // Smoothly move to the target position
    setPosition({ x: targetX, y: targetY });
    
    // After movement completes (400ms), show hover effect
    setTimeout(() => {
      setAction('hovering');
      
      // Add hover effect to the element
      element.classList.add('bot-highlighted');
      
      // After hover (300ms), show click animation
      setTimeout(() => {
        setAction('clicking');
        
        // After click animation (300ms), the actual navigation happens
        setTimeout(() => {
          element.classList.remove('bot-highlighted');
          setAction('pointing');
          
          // Stay visible briefly
          setTimeout(() => {
            setAction('idle');
          }, 1000);
        }, 300);
      }, 300); // Hover for 300ms before clicking
    }, 400); // Wait 400ms for cursor to reach target
  }, []);

  const moveCursorToElement = React.useCallback((element) => {
    const rect = element.getBoundingClientRect();
    const targetX = rect.left + rect.width / 2;
    const targetY = rect.top + rect.height / 2;
    
    // Show cursor at starting position if not already visible
    if (!isActive) {
      setIsActive(true);
      setPosition({ x: targetX - 200, y: targetY - 100 }); // Start from nearby
      
      // Small delay before moving to show starting position
      setTimeout(() => {
        animateToTarget(targetX, targetY, element);
      }, 300);
    } else {
      // Already active, just move to new position
      animateToTarget(targetX, targetY, element);
    }
  }, [isActive, animateToTarget]);

  // Animate cursor during scroll
  const animateScroll = React.useCallback((direction, fromY, toY, isLast) => {
    setIsActive(true);
    setAction('moving');
    
    const viewportHeight = window.innerHeight;
    
    // Position cursor on the right side of screen, moving vertically
    const startX = window.innerWidth - 100; // Right side, 100px from edge
    let startY, endY;
    
    if (direction === 'down') {
      // Start from top-middle, move down
      startY = viewportHeight * 0.3;
      endY = isLast ? viewportHeight * 0.9 : viewportHeight * 0.7;
    } else {
      // Start from bottom-middle, move up
      startY = viewportHeight * 0.7;
      endY = isLast ? viewportHeight * 0.1 : viewportHeight * 0.3;
    }
    
    // Set initial position
    setPosition({ x: startX, y: startY });
    
    // Animate cursor moving down/up along the right edge
    const duration = isLast ? 1200 : 800; // Longer for "last" scroll
    const steps = 20;
    const stepDuration = duration / steps;
    const yStep = (endY - startY) / steps;
    
    let currentStep = 0;
    const animate = () => {
      if (currentStep < steps) {
        const currentY = startY + (yStep * currentStep);
        setPosition({ x: startX, y: currentY });
        currentStep++;
        setTimeout(animate, stepDuration);
      } else {
        // Scroll animation complete
        setAction('pointing');
        setTimeout(() => {
          setAction('idle');
        }, 1000);
      }
    };
    
    // Start animation after a brief delay
    setTimeout(animate, 100);
  }, []);

  useEffect(() => {
    // Listen for navigation events from the bot
    const handleNavigationEvent = (event) => {
      const { section } = event.detail;
      
      // Find the target element on the page
      let targetElement = null;
      
      if (section) {
        // Find navigation button for this section using data-section attribute
        targetElement = document.querySelector(`[data-section="${section}"]`);
        
        // Fallback: search by text content
        if (!targetElement) {
          const navButtons = document.querySelectorAll('.custio-nav-link, .nav-item, button, a');
          targetElement = Array.from(navButtons).find(btn => 
            btn.textContent.toLowerCase().includes(section.toLowerCase())
          );
        }
      }
      
      if (targetElement) {
        currentSection.current = section;
        moveCursorToElement(targetElement);
      }
    };

    // Listen for scroll events from the bot
    const handleScrollEvent = (event) => {
      const { direction, fromY, toY, isLast } = event.detail;
      animateScroll(direction, fromY, toY, isLast);
    };

    // Listen for bot speaking events
    const handleBotSpeaking = (event) => {
      setIsActive(true);
      setAction('pointing');
      
      // If bot is explaining something, keep cursor visible longer
      setTimeout(() => {
        if (action !== 'moving') {
          setAction('idle');
        }
      }, 3000);
    };

    window.addEventListener('bot-navigate', handleNavigationEvent);
    window.addEventListener('bot-scroll', handleScrollEvent);
    window.addEventListener('bot-speaking', handleBotSpeaking);
    
    return () => {
      window.removeEventListener('bot-navigate', handleNavigationEvent);
      window.removeEventListener('bot-scroll', handleScrollEvent);
      window.removeEventListener('bot-speaking', handleBotSpeaking);
    };
  }, [action, moveCursorToElement, animateScroll]);

  // Add trail effect as cursor moves
  useEffect(() => {
    if (action === 'moving' && isActive) {
      setTrail(prevTrail => {
        const newTrail = [...prevTrail, { ...position, id: Date.now() }];
        // Keep only last 5 trail positions
        return newTrail.length > 5 ? newTrail.slice(1) : newTrail;
      });
      
      // Clear trail after movement
      const timer = setTimeout(() => {
        setTrail([]);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, action, isActive]);

  if (!isActive) return null;

  return (
    <>
      {/* Trail effect */}
      {trail.map((pos, index) => (
        <div
          key={pos.id}
          className="cursor-trail"
          style={{
            left: `${pos.x}px`,
            top: `${pos.y}px`,
            opacity: (index + 1) / trail.length * 0.3,
            transform: `translate(-16px, -16px) scale(${0.5 + (index / trail.length) * 0.5})`,
          }}
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path 
              d="M5 5 L5 25 L11 19 L14 27 L18 25 L15 17 L23 17 Z" 
              fill="#4CAF50"
              opacity="0.4"
            />
          </svg>
        </div>
      ))}
      
      {/* Main cursor */}
      <div 
        className={`bot-cursor ${action}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        <div className="cursor-pointer">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path 
              d="M5 5 L5 25 L11 19 L14 27 L18 25 L15 17 L23 17 Z" 
              fill="currentColor"
              stroke="white"
              strokeWidth="2"
            />
          </svg>
        </div>
        
        {action === 'clicking' && (
          <div className="click-ripple"></div>
        )}
        
        <div className="cursor-label">
          {action === 'moving' && '🎯 Moving...'}
          {action === 'hovering' && '👀 Looking at menu'}
          {action === 'clicking' && '👆 Clicking...'}
          {action === 'pointing' && '👉 Navigating...'}
          {action === 'idle' && '🤖 AI Assistant'}
        </div>
      </div>
    </>
  );
};

export default BotCursor;

